import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle, labelStyle } from '../theme';

/**
 * WaitListPanel — manage patient waiting list.
 *
 * Props:
 *   API_BASE — base API url
 */
const WaitListPanel = ({ API_BASE }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    patientName: '', phone: '', preferredDate: '', preferredTime: '',
    treatment: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/waitlist`);
      setItems(res.data || []);
    } catch (e) { setItems([]); }
    setLoading(false);
  };

  useEffect(() => { fetchList(); }, []); // eslint-disable-line

  const handleAdd = async () => {
    if (!form.patientName) return alert('Patient name is required');
    setSaving(true);
    try {
      await axios.post(`${API_BASE}/waitlist`, form);
      setForm({ patientName: '', phone: '', preferredDate: '', preferredTime: '', treatment: '', notes: '' });
      setShowForm(false);
      fetchList();
    } catch (e) { alert(e.response?.data?.error || 'Failed to add'); }
    setSaving(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API_BASE}/waitlist/${id}`, { status });
      fetchList();
    } catch (e) { alert('Update failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove from waitlist?')) return;
    try {
      await axios.delete(`${API_BASE}/waitlist/${id}`);
      fetchList();
    } catch (e) { alert('Delete failed'); }
  };

  const statusConfig = {
    waiting:   { bg: '#fef9c3', color: '#a16207', label: 'Waiting' },
    notified:  { bg: '#dbeafe', color: '#1e40af', label: 'Notified' },
    booked:    { bg: '#d1fae5', color: '#065f46', label: 'Booked' },
    cancelled: { bg: '#f1f3f5', color: '#6b7785', label: 'Cancelled' },
  };

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, color: theme.navy }}>Waiting List ({items.length})</h3>
        <button onClick={() => setShowForm(!showForm)}
          style={{
            padding: '9px 20px', backgroundColor: showForm ? '#6c757d' : theme.primary,
            color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700',
          }}>
          {showForm ? 'Cancel' : '+ Add to Waitlist'}
        </button>
      </div>

      {/* Add Form */}
      {showForm && (
        <div style={{ ...cardStyle, marginBottom: '16px', borderTop: `4px solid ${theme.primary}` }}>
          <h4 style={{ margin: '0 0 12px', color: theme.navy }}>Add to Waitlist</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <div>
              <label style={labelStyle}>Patient Name *</label>
              <input value={form.patientName} onChange={e => setForm({ ...form, patientName: e.target.value })} placeholder="Name" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Phone" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Treatment</label>
              <input value={form.treatment} onChange={e => setForm({ ...form, treatment: e.target.value })} placeholder="Treatment needed" style={inputStyle} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '10px', marginBottom: '12px' }}>
            <div>
              <label style={labelStyle}>Preferred Date</label>
              <input type="date" value={form.preferredDate} onChange={e => setForm({ ...form, preferredDate: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Preferred Time</label>
              <input type="time" value={form.preferredTime} onChange={e => setForm({ ...form, preferredTime: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Notes</label>
              <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any notes..." style={inputStyle} />
            </div>
          </div>
          <button onClick={handleAdd} disabled={saving}
            style={{
              padding: '10px 24px', backgroundColor: theme.success, color: 'white',
              border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700',
            }}>
            {saving ? 'Adding...' : '+ Add'}
          </button>
        </div>
      )}

      {/* List */}
      {loading ? <p style={{ color: theme.textMuted }}>Loading...</p> :
        items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#bbb', backgroundColor: '#fafafa', borderRadius: '10px' }}>
            No patients on the waitlist
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {items.map(item => {
              const sc = statusConfig[item.status] || statusConfig.waiting;
              return (
                <div key={item._id} style={{
                  ...cardStyle, padding: '14px 18px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                      <span style={{ fontWeight: '700', color: theme.navy, fontSize: '0.95rem' }}>{item.patientName}</span>
                      <span style={{
                        padding: '2px 10px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '700',
                        backgroundColor: sc.bg, color: sc.color,
                      }}>{sc.label}</span>
                    </div>
                    <div style={{ fontSize: '0.82rem', color: theme.textMuted, display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                      {item.phone && <span>Ph: {item.phone}</span>}
                      {item.treatment && <span>Tx: {item.treatment}</span>}
                      {item.preferredDate && <span>Date: {fmtDate(item.preferredDate)}</span>}
                      {item.preferredTime && <span>Time: {item.preferredTime}</span>}
                      {item.notes && <span>Notes: {item.notes}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    {item.status !== 'notified' && (
                      <button onClick={() => updateStatus(item._id, 'notified')}
                        style={{ padding: '4px 10px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.74rem', fontWeight: '600' }}>
                        Notify
                      </button>
                    )}
                    {item.status !== 'booked' && (
                      <button onClick={() => updateStatus(item._id, 'booked')}
                        style={{ padding: '4px 10px', backgroundColor: theme.success, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.74rem', fontWeight: '600' }}>
                        Book
                      </button>
                    )}
                    {item.status !== 'cancelled' && (
                      <button onClick={() => updateStatus(item._id, 'cancelled')}
                        style={{ padding: '4px 10px', backgroundColor: '#6b7785', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.74rem', fontWeight: '600' }}>
                        Cancel
                      </button>
                    )}
                    <button onClick={() => handleDelete(item._id)}
                      style={{ padding: '4px 10px', backgroundColor: theme.danger, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.74rem', fontWeight: '600' }}>
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      }
    </div>
  );
};

export default WaitListPanel;
